import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import useLocalStorage from '../hooks/useLocalStorage';
import { expensesService } from '../services/supabaseService';

const ExpenseContext = createContext();

const expenseReducer = (state, action) => {
  switch (action.type) {
    case 'SET_EXPENSES':
      return {
        ...state,
        expenses: action.payload
      };
    case 'ADD_EXPENSE':
      const newExpense = {
        id: action.payload.id || Date.now().toString(),
        amount: action.payload.amount,
        description: action.payload.description,
        category: action.payload.category || 'General',
        playerId: action.payload.playerId || null,
        playerName: action.payload.playerName || null,
        timestamp: action.payload.timestamp || new Date().toISOString()
      };
      return {
        ...state,
        expenses: [...state.expenses, newExpense]
      };
    case 'UPDATE_EXPENSE':
      return {
        ...state,
        expenses: state.expenses.map(expense =>
          expense.id === action.payload.id
            ? { ...expense, ...action.payload.updates }
            : expense
        )
      };
    case 'DELETE_EXPENSE':
      return {
        ...state,
        expenses: state.expenses.filter(expense => expense.id !== action.payload.id)
      };
    case 'CLEAR_ALL_EXPENSES':
      return {
        ...state,
        expenses: [],
        searchTerm: ''
      };
    case 'SET_SEARCH_TERM':
      return {
        ...state,
        searchTerm: action.payload
      };
    case 'MIGRATE_EXPENSES':
      return {
        ...state,
        expenses: action.payload.map(expense => ({
          ...expense,
          playerId: expense.playerId || null,
          playerName: expense.playerName || null
        }))
      };
    default:
      return state;
  }
};

const initialState = {
  expenses: [],
  searchTerm: ''
};

import { isSupabaseConfigured } from '../lib/supabase';

export const ExpenseProvider = ({ children, sessionId = null }) => {
  const [state, dispatch] = useReducer(expenseReducer, initialState);
  const storageKey = sessionId ? `miscExpenses_${sessionId}` : 'miscExpenses';
  const [storedExpenses, setStoredExpenses] = useLocalStorage(storageKey, []);
  const [useSupabase, setUseSupabase] = useState(isSupabaseConfigured() && sessionId);

  // Load expenses on mount
  useEffect(() => {
    const loadExpenses = async () => {
      if (useSupabase && sessionId) {
        try {
          const expenses = await expensesService.getBySession(sessionId);
          const needsMigration = expenses.some(expense => 
            expense.playerId === undefined || expense.playerName === undefined
          );
          
          if (needsMigration) {
            dispatch({ type: 'MIGRATE_EXPENSES', payload: expenses });
          } else {
            dispatch({ type: 'SET_EXPENSES', payload: expenses });
          }
        } catch (error) {
          console.error('Error loading expenses from Supabase:', error);
          setUseSupabase(false);
          const needsMigration = storedExpenses.some(expense => 
            expense.playerId === undefined || expense.playerName === undefined
          );
          
          if (needsMigration) {
            dispatch({ type: 'MIGRATE_EXPENSES', payload: storedExpenses });
          } else {
            dispatch({ type: 'SET_EXPENSES', payload: storedExpenses });
          }
        }
      } else {
        const needsMigration = storedExpenses.some(expense => 
          expense.playerId === undefined || expense.playerName === undefined
        );
        
        if (needsMigration) {
          dispatch({ type: 'MIGRATE_EXPENSES', payload: storedExpenses });
        } else {
          dispatch({ type: 'SET_EXPENSES', payload: storedExpenses });
        }
      }
    };

    loadExpenses();
  }, [sessionId]);

  // Sync to localStorage if not using Supabase
  useEffect(() => {
    if (!useSupabase && state.expenses.length >= 0) {
      setStoredExpenses(state.expenses);
    }
  }, [state.expenses, useSupabase, setStoredExpenses]);

  const addExpense = async (amount, description, category = 'General', playerId = null, playerName = null) => {
    const expenseData = {
      amount,
      description,
      category,
      playerId,
      playerName,
    };

    if (useSupabase && sessionId) {
      try {
        const newExpense = await expensesService.create(sessionId, expenseData);
        dispatch({ type: 'ADD_EXPENSE', payload: newExpense });
      } catch (error) {
        console.error('Error creating expense:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'ADD_EXPENSE', payload: expenseData });
    }
  };

  const updateExpense = async (id, updates) => {
    if (useSupabase) {
      try {
        const updatedExpense = await expensesService.update(id, updates);
        dispatch({ type: 'UPDATE_EXPENSE', payload: { id, updates: updatedExpense } });
      } catch (error) {
        console.error('Error updating expense:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'UPDATE_EXPENSE', payload: { id, updates } });
    }
  };

  const deleteExpense = async (id) => {
    if (useSupabase) {
      try {
        await expensesService.delete(id);
        dispatch({ type: 'DELETE_EXPENSE', payload: { id } });
      } catch (error) {
        console.error('Error deleting expense:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'DELETE_EXPENSE', payload: { id } });
    }
  };

  const clearAllExpenses = async () => {
    if (useSupabase && sessionId) {
      try {
        await expensesService.clearAll(sessionId);
        dispatch({ type: 'CLEAR_ALL_EXPENSES' });
      } catch (error) {
        console.error('Error clearing expenses:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'CLEAR_ALL_EXPENSES' });
      setStoredExpenses([]);
    }
  };

  const setSearchTerm = (term) => {
    dispatch({ type: 'SET_SEARCH_TERM', payload: term });
  };

  const getFilteredExpenses = () => {
    if (!state.searchTerm) return state.expenses;
    return state.expenses.filter(expense =>
      expense.description.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
      expense.category.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
      (expense.playerName && expense.playerName.toLowerCase().includes(state.searchTerm.toLowerCase()))
    );
  };

  const getTotalExpenses = () => {
    return state.expenses.reduce((total, expense) => total + expense.amount, 0);
  };

  const getExpensesByCategory = () => {
    const categories = {};
    state.expenses.forEach(expense => {
      if (!categories[expense.category]) {
        categories[expense.category] = [];
      }
      categories[expense.category].push(expense);
    });
    return categories;
  };

  const getExpensesByPlayer = () => {
    const playerExpenses = {
      unlinked: []
    };
    
    state.expenses.forEach(expense => {
      if (expense.playerId && expense.playerName) {
        if (!playerExpenses[expense.playerId]) {
          playerExpenses[expense.playerId] = {
            playerName: expense.playerName,
            expenses: []
          };
        }
        playerExpenses[expense.playerId].expenses.push(expense);
      } else {
        playerExpenses.unlinked.push(expense);
      }
    });
    
    return playerExpenses;
  };

  const getPlayerExpenseTotal = (playerId) => {
    return state.expenses
      .filter(expense => expense.playerId === playerId)
      .reduce((total, expense) => total + expense.amount, 0);
  };

  const value = {
    ...state,
    addExpense,
    updateExpense,
    deleteExpense,
    clearAllExpenses,
    setSearchTerm,
    getFilteredExpenses,
    getTotalExpenses,
    getExpensesByCategory,
    getExpensesByPlayer,
    getPlayerExpenseTotal
  };

  return (
    <ExpenseContext.Provider value={value}>
      {children}
    </ExpenseContext.Provider>
  );
};

export const useExpense = () => {
  const context = useContext(ExpenseContext);
  if (!context) {
    throw new Error('useExpense must be used within an ExpenseProvider');
  }
  return context;
};

export default ExpenseContext;
